import { useEffect } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useLogin } from '@/data/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

const schema = z.object({
  email: z.string().email('Bitte gültige E-Mail eingeben.'),
  password: z.string().min(1, 'Passwort eingeben.'),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  useDocumentTitle('Login');
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { verified?: string };
  const login = useLogin();

  useEffect(() => {
    if (search.verified === '1') {
      toast.success('E-Mail bestätigt – du kannst dich jetzt einloggen.');
    }
  }, [search.verified]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    try {
      await login.mutateAsync(values);
      void navigate({ to: '/' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Login fehlgeschlagen');
    }
  };

  return (
    <>
      <h1 className="text-2xl font-semibold">Willkommen zurück</h1>
      <p className="mt-1 text-sm text-[var(--muted-foreground)]">
        Logge dich ein, um deine Tauchgänge zu sehen.
      </p>

      <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="email">E-Mail</Label>
          <Input id="email" type="email" autoComplete="email" autoFocus {...register('email')} />
          {errors.email && <p className="text-xs text-[var(--destructive)]">{errors.email.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Passwort</Label>
          <Input id="password" type="password" autoComplete="current-password" {...register('password')} />
          {errors.password && <p className="text-xs text-[var(--destructive)]">{errors.password.message}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={login.isPending}>
          {login.isPending ? <Spinner /> : 'Einloggen'}
        </Button>

        <div className="text-center">
          <Link to="/forgot-password" className="text-xs text-ocean-700 hover:underline">
            Passwort vergessen?
          </Link>
        </div>
      </form>

      <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
        Noch kein Konto?{' '}
        <Link to="/register" className="font-medium text-ocean-700 hover:underline">
          Jetzt registrieren
        </Link>
      </p>
    </>
  );
}
